import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Phone, MapPin, Calendar, ArrowLeft, Star, ArrowRight } from 'lucide-react';
import BeforeAfterSlider from '../components/BeforeAfterSlider';
import './ProjectDetail.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Service type mapping
const SERVICE_LABELS = {
  'Projekte': 'Gartenprojekt',
  'Gartenpflege': 'Gartenpflege',
  'Baumfällung': 'Baumfällung & Baumpflege',
  'Erdarbeiten': 'Baggerarbeiten / Erdarbeiten',
  'Entsorgung': 'Entsorgung',
  'Hochdruckreinigung': 'Hochdruckreinigung',
  'Neuigkeiten': 'Aktuelles',
  'Angebote': 'Angebot',
  'Ratgeber': 'Ratgeber'
};

// Service links
const SERVICE_LINKS = {
  'Gartenpflege': '/leistungen/gartenpflege',
  'Baumfällung': '/leistungen/baumfaellung',
  'Erdarbeiten': '/leistungen/erdarbeiten',
  'Entsorgung': '/leistungen/entsorgung',
  'Hochdruckreinigung': '/leistungen/hochdruckreinigung'
};

const ProjectDetail = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/posts/${slug}`);
        if (response.ok) {
          const data = await response.json();
          setPost(data);
        } else if (response.status === 404) {
          setError('Projekt nicht gefunden');
        }
      } catch (err) {
        console.error('Error fetching post:', err);
        setError('Fehler beim Laden');
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
  }, [slug]);

  // Get before/after images
  const getBeforeAfterImages = () => {
    if (!post?.is_before_after || !post?.images) return null;
    
    const beforeImages = post.images.filter(img => img.image_type === 'before');
    const afterImages = post.images.filter(img => img.image_type === 'after');
    
    if (beforeImages.length > 0 && afterImages.length > 0) {
      const beforeUrl = beforeImages[0].url;
      const afterUrl = afterImages[0].url;
      return {
        before: beforeUrl.startsWith('http') ? beforeUrl : `${BACKEND_URL}${beforeUrl}`,
        after: afterUrl.startsWith('http') ? afterUrl : `${BACKEND_URL}${afterUrl}`
      };
    }
    return null;
  };

  // Get all images (normal or all)
  const getAllImages = () => {
    if (!post?.images) return [];
    const imgs = post.is_before_after ? post.images : post.images.filter(img => !img.image_type || img.image_type === 'normal');
    // Transform URLs - use directly if absolute, otherwise prepend BACKEND_URL
    return imgs.map(img => ({
      ...img,
      url: img.url.startsWith('http') ? img.url : `${BACKEND_URL}${img.url}`
    }));
  };

  // Generate headline
  const generateHeadline = () => {
    if (!post) return '';
    const service = SERVICE_LABELS[post.category] || post.category;
    if (post.location) {
      return `${service} in ${post.location}`;
    }
    return post.title;
  };

  // Generate meta description
  const generateMetaDescription = () => {
    if (!post) return '';
    const service = SERVICE_LABELS[post.category] || post.category;
    const location = post.location || 'der Region Göttingen';
    return `${service} erfolgreich abgeschlossen in ${location}. Nessenius Garten & Landschaftsbau - Ihr zuverlässiger Partner für Gartenpflege.`;
  };

  if (loading) {
    return (
      <div className="project-detail-page">
        <div className="container" style={{ padding: '100px 0', textAlign: 'center' }}>
          <div className="spinner" style={{
            border: '4px solid #f3f3f3',
            borderTop: '4px solid var(--brand-dark)',
            borderRadius: '50%',
            width: '40px',
            height: '40px',
            animation: 'spin 1s linear infinite',
            margin: '0 auto'
          }}></div>
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="project-detail-page">
        <div className="container" style={{ padding: '100px 0', textAlign: 'center' }}>
          <h1>{error || 'Projekt nicht gefunden'}</h1>
          <Link to="/projekte" className="back-link" style={{ marginTop: '24px', display: 'inline-block' }}>
            ← Zurück zu allen Projekten
          </Link>
        </div>
      </div>
    );
  }

  const beforeAfter = getBeforeAfterImages();
  const images = getAllImages();
  const headline = generateHeadline();
  const metaDescription = generateMetaDescription();
  const serviceLink = SERVICE_LINKS[post.category];

  return (
    <div className="project-detail-page">
      {/* SEO Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          "name": headline,
          "description": metaDescription,
          "provider": {
            "@type": "LocalBusiness",
            "name": "Nessenius Garten & Landschaftsbau",
            "image": images.length > 0 ? images[0].url : undefined,
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "Alte Straße 17",
              "addressLocality": post.location || "Nörten-Hardenberg",
              "postalCode": "37176",
              "addressCountry": "DE"
            },
            "telephone": "+49 176 55669090",
            "priceRange": "$$",
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "5.0",
              "reviewCount": "47"
            }
          },
          "areaServed": {
            "@type": "Place",
            "name": post.location || "Göttingen und Umgebung"
          },
          "image": images.length > 0 ? images.map(img => ({
            "@type": "ImageObject",
            "url": img.url,
            "name": headline
          })) : undefined
        })}
      </script>

      {/* Back Navigation */}
      <div className="project-detail-nav">
        <div className="container">
          <Link to="/projekte" className="back-link">
            <ArrowLeft size={20} />
            Alle Projekte
          </Link>
        </div>
      </div>

      {/* Hero Image Section */}
      <section className="project-detail-hero">
        <div className="container-wide">
          {beforeAfter ? (
            <div className="project-hero-slider">
              <BeforeAfterSlider
                beforeImage={beforeAfter.before}
                afterImage={beforeAfter.after}
              />
            </div>
          ) : images.length > 0 ? (
            <div className="project-hero-image">
              <img 
                src={images[0].url} 
                alt={headline}
              />
            </div>
          ) : (
            <div className="project-hero-placeholder">
              <span>🌿</span>
            </div>
          )}
        </div>
      </section>

      {/* Content Section */}
      <section className="project-detail-content">
        <div className="container">
          <div className="project-detail-grid">
            {/* Main Content */}
            <div className="project-detail-main">
              {/* Badges */}
              <div className="project-detail-badges">
                <span className="badge badge-service">
                  {SERVICE_LABELS[post.category] || post.category}
                </span>
                {post.location && (
                  <span className="badge badge-location">
                    <MapPin size={14} />
                    {post.location}
                  </span>
                )}
              </div>

              {/* Title */}
              <h1 className="project-detail-title">{headline}</h1>

              {/* Completion Date */}
              <div className="project-detail-date">
                <Calendar size={18} />
                <span>Projekt abgeschlossen am {new Date(post.created_at).toLocaleDateString('de-DE', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric'
                })}</span>
              </div>

              {/* Description */}
              {post.content && (
                <div className="project-detail-description">
                  <p>{post.content}</p>
                </div>
              )}

              {/* Service Link */}
              {serviceLink && (
                <div className="project-service-link">
                  <span>Mehr über diese Leistung erfahren:</span>
                  <Link to={serviceLink}>
                    {SERVICE_LABELS[post.category]} →
                  </Link>
                </div>
              )}

              {/* Additional Images Gallery */}
              {images.length > 1 && (
                <div className="project-detail-gallery">
                  <h3>Weitere Bilder</h3>
                  <div className="gallery-grid">
                    {images.slice(1).map((img, index) => (
                      <div key={img.id || index} className="gallery-item">
                        <img 
                          src={img.url} 
                          alt={`${headline} - Bild ${index + 2}`}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="project-detail-sidebar">
              {/* CTA Card */}
              <div className="sidebar-cta-card">
                <h3>Ähnliches Projekt geplant?</h3>
                <p>Wir beraten Sie gerne kostenlos und unverbindlich.</p>
                
                <Link to="/kontakt" className="sidebar-cta-btn primary">
                  Angebot anfragen
                  <ArrowRight size={18} />
                </Link>
                
                <a href="tel:+4917655669090" className="sidebar-cta-btn secondary">
                  <Phone size={18} />
                  0176 - 556 690 90
                </a>
                
                <a 
                  href="https://wa.me/4917655669090?text=Hallo,%20ich%20interessiere%20mich%20für%20ein%20Gartenprojekt." 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="sidebar-cta-btn whatsapp"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                  </svg>
                  WhatsApp schreiben
                </a>
              </div>

              {/* Reviews Teaser */}
              <div className="sidebar-reviews-card">
                <div className="reviews-header">
                  <div className="reviews-stars">
                    {[1,2,3,4,5].map(i => (
                      <Star key={i} size={18} fill="#ffc107" color="#ffc107" />
                    ))}
                  </div>
                  <span className="reviews-rating">5.0</span>
                </div>
                <p className="reviews-text">
                  "Schnell, zuverlässig und faire Preise. Sehr zu empfehlen!"
                </p>
                <span className="reviews-source">— Google Bewertungen (47 Bewertungen)</span>
                <Link to="/bewertungen" className="reviews-link">
                  Alle Bewertungen ansehen →
                </Link>
              </div>

              {/* Service Area Teaser */}
              {post.location && (
                <div className="sidebar-area-card">
                  <h4>Wir arbeiten auch in Ihrer Nähe</h4>
                  <p>Unser Servicegebiet umfasst {post.location} und die gesamte Region Göttingen.</p>
                  <Link to="/servicegebiet">Servicegebiet ansehen →</Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Related Projects CTA */}
      <section className="project-detail-cta">
        <div className="container">
          <div className="cta-content">
            <h2>Weitere Projekte ansehen</h2>
            <p>Entdecken Sie mehr unserer abgeschlossenen Arbeiten in Ihrer Region.</p>
            <Link to="/projekte" className="cta-btn">
              Alle Projekte ansehen
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProjectDetail;
